// Read-optimized alternative to CalendarView's time-grid — a plain
// chronological agenda per day (ideas + flight/activity bands merged and
// sorted by start time), for screens where the grid is hard to read (mobile).
// Deliberately does NOT reimplement drag/resize — every row action (edit,
// move, remove) calls the exact same handlers ItineraryScreen already wires
// to CalendarView, so both views stay backed by one source of truth with no
// new persistence logic here.
import React from 'react';
import { PALETTE } from '../../theme';
import { Icon } from '../../components/UI';
import { bookingInfoForIdea } from '../../utils/bookingLinks';
import { parseFlightHM, fmtTime } from './logic';
import { CAL_CAT_COLORS, unplacedColorFor, sortIdsByLeg } from './CalendarView';

const FLIGHT_COLOR = { bg: '#EFF6FF', border: '#93C5FD', mono: '#1E40AF' };
const ACTIVITY_COLOR = { bg: '#E6F7F5', border: '#2A9D8F', mono: '#1B6E63' };
// 100vh over-measures on mobile browsers while the URL bar is visible; prefer dvh
const VH_FULL = typeof CSS !== 'undefined' && CSS.supports?.('height', '100dvh') ? '100dvh' : '100vh';

const rowBase = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left',
  appearance: 'none', border: 0, cursor: 'pointer', padding: '10px 20px',
  borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, background: 'transparent',
};

function RowMenu({ open, onClose, actions }) {
  if (!open) return null;
  return (
    <div onClick={e => e.stopPropagation()} style={{
      position: 'absolute', top: '100%', right: 20, zIndex: 20, marginTop: 2,
      background: PALETTE.card, borderRadius: 10, border: `1px solid ${PALETTE.rule}`,
      boxShadow: '0 8px 24px rgba(34,31,26,0.14)', overflow: 'hidden', minWidth: 160,
    }}>
      {actions.map((a, i) => (
        <button key={i} onClick={() => { a.onClick(); onClose(); }} style={{
          display: 'block', width: '100%', textAlign: 'left', appearance: 'none', border: 0,
          background: 'transparent', cursor: 'pointer', padding: '9px 14px',
          fontFamily: 'Inter', fontSize: 12, color: a.danger ? PALETTE.red : PALETTE.ink,
        }}>{a.label}</button>
      ))}
    </div>
  );
}

function IdeaRow({ idea, start, dur, onEdit, onMoveToParking, onRemove }) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const cc = CAL_CAT_COLORS[idea.cat] || CAL_CAT_COLORS.Custom;
  const bookingInfo = bookingInfoForIdea(idea, undefined);
  const trackBooking = !!(bookingInfo || idea.requiresBooking);
  return (
    <div style={{ position: 'relative', borderLeft: `3px solid ${cc.border}`, ...rowBase, background: `${cc.bg}66`, padding: 0 }}>
      <button onClick={onEdit} style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px', textAlign: 'left' }}>
        <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: cc.text, width: 44, flexShrink: 0 }}>
          {start != null ? fmtTime(start) : ''}
        </span>
        <span style={{ flex: 1, fontFamily: 'Inter', fontSize: 14, color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {idea.title}
        </span>
        {trackBooking && (
          <span title={idea.requiresBooking ? 'Needs booking' : ''} style={{ width: 6, height: 6, borderRadius: 999, background: PALETTE.accent, flexShrink: 0 }} />
        )}
        <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, flexShrink: 0 }}>
          {dur < 1 ? `${dur * 60}m` : `${dur}h`}
        </span>
      </button>
      <button
        onClick={() => setMenuOpen(o => !o)}
        aria-label="Item actions"
        style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: '10px 20px 10px 6px', flexShrink: 0, display: 'flex' }}
      >
        <Icon name="more" size={14} />
      </button>
      <RowMenu open={menuOpen} onClose={() => setMenuOpen(false)} actions={[
        { label: 'Edit', onClick: onEdit },
        { label: 'Move to parking lot', onClick: onMoveToParking },
        { label: 'Remove from trip', onClick: onRemove, danger: true },
      ]} />
    </div>
  );
}

function ParkedRow({ idea, displayDays, onMoveToDay, onEdit, onRemove, compact = false }) {
  const [dayPickerOpen, setDayPickerOpen] = React.useState(false);
  const cc = unplacedColorFor(idea);
  // Compact (floating panel) rows stack the city tag above the title and use
  // icon-only actions — the panel is only ~240px wide, too narrow for city
  // tag + title + a text "+ Add to day" button + edit icon on one line.
  if (compact) {
    return (
      <div style={{ position: 'relative', borderLeft: `3px solid ${cc.border}`, background: `${cc.bg}66`, borderRadius: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 8px' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {idea.destCity && (
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: 8, letterSpacing: 0.3, textTransform: 'uppercase', color: cc.text, opacity: 0.75 }}>{idea.destCity}</span>
            )}
            <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {idea.title}
            </span>
          </div>
          <button onClick={() => setDayPickerOpen(o => !o)} title="Add to day" style={{
            appearance: 'none', border: 0, background: 'transparent',
            color: PALETTE.inkSoft, cursor: 'pointer', padding: 3, flexShrink: 0, display: 'flex',
          }}><Icon name="plus" size={13} /></button>
          <button
            onClick={() => { setDayPickerOpen(false); onEdit(); }}
            aria-label="Edit"
            style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: 3, flexShrink: 0, display: 'flex' }}
          >
            <Icon name="pencil" size={12} />
          </button>
        </div>
        <RowMenu open={dayPickerOpen} onClose={() => setDayPickerOpen(false)} actions={[
          ...displayDays.map((day, di) => ({ label: `${day.label} · ${day.date}`, onClick: () => onMoveToDay(di) })),
          { label: 'Remove from trip', onClick: onRemove, danger: true },
        ]} />
      </div>
    );
  }
  return (
    <div style={{ position: 'relative', borderLeft: `3px solid ${cc.border}` }}>
      <div style={{ ...rowBase, cursor: 'default' }}>
        {idea.destCity && (
          <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.3, textTransform: 'uppercase', color: cc.text, opacity: 0.75, flexShrink: 0 }}>{idea.destCity}</span>
        )}
        <span style={{ flex: 1, fontFamily: 'Inter', fontSize: 14, color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {idea.title}
        </span>
        <button onClick={() => setDayPickerOpen(o => !o)} style={{
          appearance: 'none', border: `1px solid ${PALETTE.rule}`, background: 'transparent',
          color: PALETTE.inkSoft, borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
          fontFamily: 'Inter', fontSize: 11, flexShrink: 0,
        }}>+ Add to day</button>
        <button
          onClick={() => { setDayPickerOpen(false); onEdit(); }}
          aria-label="Edit"
          style={{ appearance: 'none', border: 0, background: 'transparent', cursor: 'pointer', color: PALETTE.inkMute, padding: '2px 2px 2px 6px', flexShrink: 0, display: 'flex' }}
        >
          <Icon name="pencil" size={13} />
        </button>
      </div>
      <RowMenu open={dayPickerOpen} onClose={() => setDayPickerOpen(false)} actions={[
        ...displayDays.map((day, di) => ({ label: `${day.label} · ${day.date}`, onClick: () => onMoveToDay(di) })),
        { label: 'Remove from trip', onClick: onRemove, danger: true },
      ]} />
    </div>
  );
}

export function ListView({
  displayDays, localOverrides = {}, onEditItem, onMoveToParking, onRemoveFromTrip,
  onMoveToDay, flightByDay = {}, activityByDay = {}, accommodations = {}, accomBookedByDay = {},
  dayNotes = {}, onOpenLeg, displayOverflow = [], allIdeas = [],
}) {
  const [parkingOpen, setParkingOpen] = React.useState(false);
  const effectiveStart = (slot) => localOverrides[slot.idea.id]?.startTime ?? slot.start;
  const effectiveDur = (slot) => localOverrides[slot.idea.id]?.dur ?? slot.idea.dur;
  const sortedOverflow = sortIdsByLeg(displayOverflow, allIdeas);

  return (
    <div style={{ position: 'relative' }}>
      {/* Unplaced toggle — floats above the day list instead of living at
          the bottom of a potentially long agenda, matching CalendarView's
          parking-lot button so both views behave the same way. */}
      <div style={{ position: 'absolute', top: 8, right: 10, zIndex: 15 }}>
        <button
          onClick={() => setParkingOpen(o => !o)}
          aria-expanded={parkingOpen}
          style={{
            appearance: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 10px', borderRadius: 999,
            background: PALETTE.paper,
            border: `1.5px solid ${PALETTE.rule}`,
            color: PALETTE.inkMute,
            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
            fontFamily: 'JetBrains Mono', fontSize: 9, letterSpacing: 0.6, textTransform: 'uppercase',
          }}
        >
          {`Unplaced${displayOverflow.length > 0 ? ` · ${displayOverflow.length}` : ''}`}
          <Icon name={parkingOpen ? 'arrowUp' : 'arrowDn'} size={10} />
        </button>
        {parkingOpen && (
          <div style={{
            position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 16,
            width: 240, maxHeight: 320, overflowY: 'auto', scrollbarWidth: 'thin',
            background: PALETTE.paper, border: `0.5px solid ${PALETTE.rule}`, borderRadius: 12,
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)', padding: 8,
            display: 'flex', flexDirection: 'column', gap: 5,
          }}>
            {sortedOverflow.length === 0 ? (
              <span style={{ fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkMute, fontStyle: 'italic', padding: '8px 6px' }}>
                Everything selected is on the calendar.
              </span>
            ) : sortedOverflow.map(id => {
              const idea = allIdeas.find(i => i.id === id);
              if (!idea) return null;
              return (
                <ParkedRow
                  key={id}
                  idea={idea}
                  displayDays={displayDays}
                  onMoveToDay={(dayIdx) => onMoveToDay(id, dayIdx)}
                  onEdit={() => onEditItem(idea)}
                  onRemove={() => onRemoveFromTrip(id)}
                  compact
                />
              );
            })}
          </div>
        )}
      </div>

      <div style={{ height: `calc(${VH_FULL} - 145px)`, overflowY: 'auto', paddingBottom: 24 }}>
      {displayDays.map((day, dayIdx) => {
        const accom = accommodations[dayIdx];
        const rows = [
          ...day.items.map(slot => ({ type: 'idea', start: effectiveStart(slot), slot })),
          ...(flightByDay[dayIdx] || []).map(band => ({ type: 'flight', start: parseFlightHM(band.depTime) ?? parseFlightHM(band.arrTime) ?? 0, band })),
          ...(activityByDay[dayIdx] || []).map(act => ({ type: 'activity', start: parseFlightHM(act.time) ?? 0, act })),
        ].sort((a, b) => a.start - b.start);

        return (
          <div key={dayIdx}>
            <div style={{ padding: '12px 20px 6px', display: 'flex', alignItems: 'baseline', gap: 8, background: PALETTE.paperDim }}>
              <span style={{ fontFamily: 'Fraunces', fontSize: 17, color: PALETTE.ink }}>{day.label}</span>
              <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: PALETTE.inkMute }}>{day.date}</span>
              {day.destCity && (
                <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', color: PALETTE.accent }}>{day.destCity}</span>
              )}
              {dayNotes[dayIdx] && (
                <span style={{ fontFamily: 'Inter', fontSize: 11, color: PALETTE.inkMute, fontStyle: 'italic', marginLeft: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {dayNotes[dayIdx]}
                </span>
              )}
            </div>
            {accom?.name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 20px', borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, fontFamily: 'Inter', fontSize: 12, color: PALETTE.inkSoft }}>
                <Icon name="bed" size={12} style={{ color: PALETTE.inkMute, flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{accom.name}</span>
                <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: accomBookedByDay[dayIdx] ? '#16A34A' : PALETTE.inkMute, marginLeft: 'auto', flexShrink: 0 }}>
                  {accomBookedByDay[dayIdx] ? '✓ booked' : 'not booked'}
                </span>
              </div>
            )}
            {rows.length === 0 && (
              <div style={{ padding: '10px 20px', borderBottom: `0.5px solid ${PALETTE.ruleSoft}`, fontFamily: 'Inter', fontSize: 12, color: PALETTE.rule }}>
                Nothing planned yet.
              </div>
            )}
            {rows.map((row, ri) => {
              if (row.type === 'flight') {
                const band = row.band;
                const dep = parseFlightHM(band.depTime), arr = parseFlightHM(band.arrTime);
                const route = [band.origin, band.dest].filter(Boolean).join(' → ');
                return (
                  <button key={`f${ri}`} onClick={() => onOpenLeg?.('flight', band)} style={{ ...rowBase, borderLeft: `3px solid ${FLIGHT_COLOR.border}`, background: FLIGHT_COLOR.bg }}>
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: FLIGHT_COLOR.mono, width: 44, flexShrink: 0 }}>
                      {dep != null ? fmtTime(dep) : ''}
                    </span>
                    <span style={{ flex: 1, fontFamily: 'Inter', fontSize: 14, color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      ✈️ {route || 'Flight'}{band.flightNo ? ` · ${band.flightNo}` : ''}
                    </span>
                    {arr != null && <span style={{ fontFamily: 'JetBrains Mono', fontSize: 9, color: PALETTE.inkMute, flexShrink: 0 }}>lands {fmtTime(arr)}</span>}
                  </button>
                );
              }
              if (row.type === 'activity') {
                const t = parseFlightHM(row.act.time);
                return (
                  <button key={`a${ri}`} onClick={() => onOpenLeg?.('activity', row.act)} style={{ ...rowBase, borderLeft: `3px solid ${ACTIVITY_COLOR.border}`, background: ACTIVITY_COLOR.bg }}>
                    <span style={{ fontFamily: 'JetBrains Mono', fontSize: 10, color: ACTIVITY_COLOR.mono, width: 44, flexShrink: 0 }}>
                      {t != null ? fmtTime(t) : ''}
                    </span>
                    <span style={{ flex: 1, fontFamily: 'Inter', fontSize: 14, color: PALETTE.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      🎟️ {row.act.title || 'Activity'}
                    </span>
                  </button>
                );
              }
              const { slot } = row;
              return (
                <IdeaRow
                  key={slot.idea.id}
                  idea={slot.idea}
                  start={effectiveStart(slot)}
                  dur={effectiveDur(slot)}
                  onEdit={() => onEditItem(slot.idea)}
                  onMoveToParking={() => onMoveToParking(slot.idea.id)}
                  onRemove={() => onRemoveFromTrip(slot.idea.id)}
                />
              );
            })}
          </div>
        );
      })}
      </div>
    </div>
  );
}
